import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChartFacade } from '@mode/charts/contract';
import { Flamingo, QueryBuilder } from '@mode/charts/util-vegas';
import { FeatureFlag, FeatureFlagsFacade, FileExportDomain, FlamingoTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS, isPresent } from '@mode/shared/util-js';
import { CsvExportEvents, recordEvent } from '@mode/shared/util-tracing';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { formatISO } from 'date-fns';
import * as FileSaver from 'file-saver';
import { of } from 'rxjs';
import { catchError, filter, first, map, mergeMap, skip, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as FileExportActions from './file-export.actions';
import * as FileExportSelectors from './file-export.selectors';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFilterFacade, ReportFilterHelpers } from '@mode/report-viewer/domain';
import { Model } from '@mode-switch/model';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ViewDataModalFacade } from '@mode/reports/domain';

@Injectable()
export class FileExportEffects {
  exportDesignerCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.chartDesignerExportCsv),
      tap((action) => recordEvent(CsvExportEvents.designerCsvExportClick({ token: action.token }))),
      switchMap((action) => {
        return this.chartFacade.createCompleteFlamingoParams(action.token).pipe(
          switchMap(({ switchViewToken, encoding }) =>
            this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
              filter(isPresent),
              withLatestFrom(
                this.flamingoFacade
                  .createPlanIdFromRefId(action.refId)
                  .pipe(switchMap((planId) => this.flamingoFacade.createCountFromPlanId(planId)))
              ),
              map(([datasetResult, count]) => ({
                datasetResult,
                encoding,
                token: action.token,
                count,
              })),
              first()
            )
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.featureFlagFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit),
            this.chartFacade.createChartTitleFromVizToken(action.token),
            this.store.select(FileExportSelectors.selectAll)
          ),
          first()
        );
      }),
      map(([{ token, encoding, datasetResult, count }, model, limit, title, exports]) => {
        const filename = this.deduplicateFilename(title, exports);
        const plan = QueryBuilder.buildCsvQuery(model, datasetResult.queryToken, encoding, limit, 0);
        return FileExportActions.chartExportCsvPlan({
          token,
          refId: token + '-csv',
          plan,
          filename,
          queryRunToken: datasetResult.queryRunToken,
          switchViewToken: datasetResult.switchViewToken,
          rowCount: count ?? -1,
          datasetColumnCount: datasetResult.metaColumns.length,
          datasetRowCount: datasetResult.rowCount ?? -1,
        });
      }),
      tap((action) => {
        recordEvent(
          CsvExportEvents.generateDesignerCsvPlan({
            traceId: action.plan.id,
            chartToken: action.token,
            switchViewToken: action.switchViewToken,
            plan: action.plan,
            rowCount: action.rowCount,
            datasetColumnCount: action.datasetColumnCount,
            datasetRowCount: action.datasetRowCount,
          })
        );
      })
    )
  );

  exportViewDataCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.viewDataChartExportCsv),
      withLatestFrom(
        this.viewDataModalFacade.title$,
        this.viewDataModalFacade.encoding$,
        this.viewDataModalFacade.queryToken$
      ),
      switchMap(([action, title, encoding, queryToken]) => {
        return this.dataViewFacade.createSwitchViewTokenByQueryToken(queryToken).pipe(
          filter(isPresent),
          switchMap((switchViewToken) =>
            this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
              filter(isPresent),
              withLatestFrom(
                this.flamingoFacade
                  .createPlanIdFromRefId(FLAMINGO_RESERVED_REFS.ViewData)
                  .pipe(switchMap((planId) => this.flamingoFacade.createCountFromPlanId(planId)))
              ),
              map(([datasetResult, count]) => ({
                title,
                datasetResult,
                encoding,
                count,
              })),
              first()
            )
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.featureFlagFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit),
            this.store.select(FileExportSelectors.selectAll)
          ),
          first()
        );
      }),
      map(([{ encoding, datasetResult, count, title }, model, limit, exports]) => {
        const filename = this.deduplicateFilename(title, exports);
        const plan = QueryBuilder.buildCsvQuery(model, datasetResult.queryToken, encoding, limit, 0);
        return FileExportActions.chartExportCsvPlan({
          token: datasetResult.queryToken, // There's not really a chart token
          refId: FLAMINGO_RESERVED_REFS.ViewData,
          plan,
          filename,
          queryRunToken: datasetResult.queryRunToken,
          switchViewToken: datasetResult.switchViewToken,
          rowCount: count ?? -1,
          datasetColumnCount: datasetResult.metaColumns.length,
          datasetRowCount: datasetResult.rowCount ?? -1,
        });
      }),
      tap((action) => {
        this.viewDataModalFacade.closeModal();
        recordEvent(
          CsvExportEvents.generateViewDataCsvPlan({
            traceId: action.plan.id,
            switchViewToken: action.switchViewToken,
            plan: action.plan,
            rowCount: action.rowCount,
            datasetColumnCount: action.datasetColumnCount,
            datasetRowCount: action.datasetRowCount,
          })
        );
      })
    )
  );

  exportReportCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.chartReportExportCsv),
      tap((action) => recordEvent(CsvExportEvents.reportCsvExportClick({ token: action.token }))),
      switchMap((action) => {
        return this.chartFacade.createCompleteFlamingoParams(action.token).pipe(
          switchMap(({ switchViewToken, encoding }) =>
            this.dataViewFacade.createDatasetBySwitchViewToken(switchViewToken).pipe(
              filter(isPresent),
              withLatestFrom(
                this.flamingoFacade
                  .createPlanIdFromRefId(action.token)
                  .pipe(switchMap((planId) => this.flamingoFacade.createCountFromPlanId(planId)))
              ),
              map(([datasetResult, count]) => ({
                datasetResult,
                encoding,
                token: action.token,
                count,
              })),
              first()
            )
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.featureFlagFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit),
            this.chartFacade.createChartTitleFromVizToken(action.token),
            this.store.select(FileExportSelectors.selectAll),
            this.reportFilterFacade.filterControls$
          ),
          first()
        );
      }),
      map(([{ token, encoding, datasetResult, count }, model, limit, title, exports, reportFilters]) => {
        const filename = this.deduplicateFilename(title, exports);
        const validFilters = Flamingo.getValidReportFilters(model, datasetResult.queryToken, reportFilters);
        encoding = ReportFilterHelpers.addReportFiltersToChartEncoding(
          Model.Views.find(model, datasetResult.queryToken),
          encoding,
          validFilters,
          false
        );
        const plan = QueryBuilder.buildCsvQuery(model, datasetResult.queryToken, encoding, limit, 0);

        return FileExportActions.chartExportCsvPlan({
          token,
          refId: token + '-csv',
          plan,
          filename,
          queryRunToken: datasetResult.queryRunToken,
          switchViewToken: datasetResult.switchViewToken,
          rowCount: count ?? -1,
          datasetColumnCount: datasetResult.metaColumns.length,
          datasetRowCount: datasetResult.rowCount ?? -1,
        });
      }),
      tap((action) => {
        recordEvent(
          CsvExportEvents.generateReportCsvPlan({
            traceId: action.plan.id,
            chartToken: action.token,
            switchViewToken: action.switchViewToken,
            datasetColumnCount: action.datasetColumnCount,
            datasetRowCount: action.datasetRowCount,
            rowCount: action.rowCount,
            plan: action.plan,
          })
        );
      })
    )
  );

  exportCsvPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.chartExportCsvPlan, FileExportActions.retryChartExportPlan),
      tap((action) => {
        this.flamingoFacade.exportCsv(action.refId, action.plan, action.queryRunToken);
        recordEvent('helix-csv-export', 'dispatch-helix-query', {
          traceId: action.plan.id,
          chartToken: action.token,
          plan: action.plan,
        });
      }),
      mergeMap((action) => {
        const isRetry = action.type === FileExportActions.retryChartExportPlan.type;
        return this.flamingoFacade.createStateFromPlanId(action.plan.id).pipe(
          /**
           * Need to first result if retry since it'll return the status of the previous execution.
           * This is because NgRx actions don't execute synchronously when dispatched from a subscription.
           */
          skip(action.type === FileExportActions.retryChartExportPlan.type ? 1 : 0),
          tap((status) =>
            recordEvent('helix-csv-export', 'wait-plan-status', {
              traceId: action.plan.id,
              chartToken: action.token,
              plan: action.plan,
              status,
            })
          ),
          filter(
            (status) => status === FlamingoTypes.PlanStatus.Succeeded || status === FlamingoTypes.PlanStatus.Failed
          ),
          first(),
          switchMap((status) => {
            switch (status) {
              case FlamingoTypes.PlanStatus.Succeeded:
                return this.flamingoFacade.createExportFromPlanId(action.plan.id).pipe(
                  map((urlOrBlob) => {
                    return this.getExportSuccessAction(action)({
                      urlOrBlob,
                      id: action.plan.id,
                      token: action.token,
                      refId: action.refId,
                    });
                  }),
                  first()
                );
              default:
                return this.flamingoFacade.createErrorFromPlanId(action.plan.id).pipe(
                  first(),
                  map((errorResponse) => {
                    return this.getExportFailedAction(action)({
                      id: action.plan.id,
                      refId: action.refId,
                      token: action.token,
                      error: new Error(errorResponse.type), // TODO: Decode this
                    });
                  })
                );
            }
          })
        );
      })
    )
  );

  retryExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.retryExport),
      tap((action) =>
        recordEvent('helix-csv-export', 'retry-csv-export', {
          traceId: action.id,
        })
      ),
      switchMap((action) =>
        this.store.select(FileExportSelectors.createExportSelector(action.id)).pipe(
          filter(isPresent),
          first(),
          map((exp) => {
            switch (exp.type) {
              case FileExportDomain.ExportType.Chart: // Add logic here to support retries of different export types
                return FileExportActions.retryChartExportPlan({
                  plan: exp.plan,
                  queryRunToken: exp.queryRunToken,
                  refId: exp.refId || '', // This should be present at this point
                  token: exp.vizToken,
                });
              default:
                return FileExportActions.retryExportFailed({
                  id: exp.id,
                  error: new Error('Unsupported type: ' + exp.type),
                  token: '',
                  refId: exp.refId || '',
                });
            }
          })
        )
      )
    )
  );

  chartExportCsvSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.chartExportCsvSuccess),
        tap((action) =>
          recordEvent(
            CsvExportEvents.csvExportSuccess({
              traceId: action.id,
              token: action.token,
              refId: action.refId,
              isRetry: false,
            })
          )
        )
      ),
    { dispatch: false }
  );

  chartExportCsvFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.chartExportCsvFailed),
        tap((action) =>
          recordEvent(
            CsvExportEvents.csvExportFailed({
              traceId: action.id,
              token: action.token,
              refId: action.refId,
              err: action.error,
              isRetry: false,
            })
          )
        )
      ),
    { dispatch: false }
  );

  retryExportCsvSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.retryExportSuccess),
        tap((action) =>
          recordEvent(
            CsvExportEvents.csvExportSuccess({
              traceId: action.id,
              token: action.token,
              refId: action.refId,
              isRetry: true,
            })
          )
        )
      ),
    { dispatch: false }
  );

  retryExportCsvFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FileExportActions.retryExportFailed),
        tap((action) =>
          recordEvent(
            CsvExportEvents.csvExportFailed({
              traceId: action.id,
              token: action.token,
              refId: action.refId,
              err: action.error,
              isRetry: true,
            })
          )
        )
      ),
    { dispatch: false }
  );

  downloadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileExportActions.downloadExport),
      mergeMap((action) => this.store.select(FileExportSelectors.createExportSelector(action.id))),
      filter(isPresent),
      tap((action) =>
        recordEvent(
          CsvExportEvents.downloadClick({
            traceId: action.id,
          })
        )
      ),
      mergeMap((exp) => {
        const urlOrBlob = exp.urlOrBlob;
        if (urlOrBlob instanceof Blob) {
          FileSaver.saveAs(urlOrBlob, exp.fileName);
          return of(FileExportActions.downloadExportSuccess({ id: exp.id, downloadSize: urlOrBlob.size }));
        } else {
          // TODO: make successful export types to avoid urlOrBlob cast
          return this.http.get(urlOrBlob as string, { responseType: 'blob' }).pipe(
            tap((csv) => {
              FileSaver.saveAs(csv, exp.fileName);
            }),
            map((csv) => {
              return FileExportActions.downloadExportSuccess({ id: exp.id, downloadSize: csv.size });
            }),
            catchError((err) => {
              return of(FileExportActions.downloadExportFailed({ id: exp.id, error: err }));
            })
          );
        }
      })
    )
  );

  downloadExportSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FileExportActions.downloadExportSuccess),
        tap((action) => {
          recordEvent(
            CsvExportEvents.downloadSuccess({
              traceId: action.id,
              downloadSize: action.downloadSize,
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  downloadExportFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FileExportActions.downloadExportFailed),
        tap((action) =>
          recordEvent(
            CsvExportEvents.downloadFailed({
              traceId: action.id,
              err: action.error,
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  private deduplicateFilename(title: Readonly<string | undefined>, exports: FileExportDomain.FileExport[]) {
    const baseFilename = `${title || 'Untitled Chart'} ${formatISO(new Date(), {
      representation: 'date',
    })}`;

    let filename = baseFilename + '.csv';
    for (let ii = 1; exports.some((exp) => exp.fileName === filename); ii++) {
      filename = baseFilename + ` (${ii}).csv`;
    }
    return filename;
  }

  private getExportSuccessAction(
    sourceAction:
      | ReturnType<typeof FileExportActions.chartExportCsvPlan>
      | ReturnType<typeof FileExportActions.retryChartExportPlan>
  ) {
    switch (sourceAction.type) {
      case FileExportActions.retryChartExportPlan.type:
        return FileExportActions.retryExportSuccess;
      case FileExportActions.chartExportCsvPlan.type:
        return FileExportActions.chartExportCsvSuccess;
    }
  }

  // Functions to support good action hygiene
  private getExportFailedAction(
    sourceAction:
      | ReturnType<typeof FileExportActions.chartExportCsvPlan>
      | ReturnType<typeof FileExportActions.retryChartExportPlan>
  ) {
    switch (sourceAction.type) {
      case FileExportActions.retryChartExportPlan.type:
        return FileExportActions.retryExportFailed;
      case FileExportActions.chartExportCsvPlan.type:
        return FileExportActions.chartExportCsvFailed;
    }
  }

  constructor(
    private actions$: Actions,
    private chartFacade: ChartFacade,
    private flamingoFacade: FlamingoFacade,
    private dataViewFacade: DataViewFacade,
    private featureFlagFacade: FeatureFlagsFacade,
    private http: HttpClient,
    private store: Store,
    private reportFilterFacade: ReportFilterFacade,
    private viewDataModalFacade: ViewDataModalFacade
  ) {}
}
